import { Inject, Injectable } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { from, Observable } from 'rxjs';
import { catchError, filter } from 'rxjs/operators';

import { ConfirmationModalComponent } from '../components/confirmation-modal/confirmation-modal.component';
import { ProgressModalComponent } from '../components/progress-modal/progress-modal.component';
import { SimpleModalComponent } from '../components/simple-modal/simple-modal.component';
import { setupModal, TypedNgbModal } from '../utils/typed-modal';

@Injectable({
  providedIn: 'root',
})
export class ModalService {
  constructor(@Inject(NgbModal) private ngbModal: TypedNgbModal) {}

  showConfirmationModal(
    message: string,
    title: string,
    confirmationInput: string,
    confirmButtonLabel?: string,
    cancelButtonLabel?: string,
  ) {
    const ref = setupModal(
      this.ngbModal.open(ConfirmationModalComponent, { size: 'sm' }),
      modal => {
        modal.title = title;
        modal.text = message;
        modal.confirmationInput = confirmationInput;
        modal.confirmButtonLabel = confirmButtonLabel;
        modal.cancelButtonLabel = cancelButtonLabel;
      },
    );

    return from(ref.result).pipe(
      filter(x => x === 'OK'),
      catchError(() => []),
    );
  }

  showQuestionModal(
    message: string,
    title: string,
    okButtonLabel?: string,
    cancelButtonLabel?: string,
    getCancelNotification?: boolean,
    showCancelButton = true,
  ) {
    const ref = setupModal(this.ngbModal.open(SimpleModalComponent, { size: 'sm' }), modal => {
      modal.title = title;
      modal.text = message;
      modal.okButtonLabel = okButtonLabel;
      modal.cancelButtonLabel = cancelButtonLabel;
      modal.showCancelButton = showCancelButton;
    });

    return from(ref.result).pipe(
      filter(x => x === 'OK' || getCancelNotification === true),
      catchError(() => (getCancelNotification === true ? ['Cancel'] : [])),
    );
  }

  showErrorModal(message: string, title: string, okButtonLabel?: string): Observable<'OK'> {
    const ref = setupModal(this.ngbModal.open(SimpleModalComponent, { size: 'sm' }), modal => {
      modal.title = title;
      modal.text = message;
      modal.okButtonLabel = okButtonLabel;
      modal.showCancelButton = false;
    });

    return from(ref.result).pipe(
      filter(x => x === 'OK'),
      catchError(() => []),
    );
  }

  /**
   * Needs to be explicitly closed - backdrop is set to 'static'
   */
  openProgressModal(title: string) {
    const ref = setupModal(
      this.ngbModal.open(ProgressModalComponent, { backdrop: 'static' }),
      modal => {
        modal.title = title;
      },
    );

    return ref.componentInstance;
  }
}
